import { motion } from "framer-motion";
import { useState } from "react";
import { Users, GitPullRequest, Plus, Minus, Clock, GitBranch, ArrowUpRight } from "lucide-react";

const RULES = [
  {
    id: "open",
    icon: Users,
    title: "Individuals only",
    short: "Solo challenge.",
    detail:
      "This is an individual challenge for the engineering team, no teams. Pick a real internal problem and ship your own skill.",
  },
  {
    id: "submit",
    icon: GitPullRequest,
    title: "Submit via Pull Request",
    short: "→ carestack.skills repo",
    detail:
      "Open a Pull Request to the carestack.skills monorepo. Follow the SKILL.md template. Include prompt, tools, logic, examples, and a short README.",
  },
];

export default function SubmissionRules() {
  const [open, setOpen] = useState("open");

  return (
    <section
      id="rules"
      className="relative py-16 lg:py-20 px-6 md:px-12 lg:px-24"
      data-testid="rules-section"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mb-16"
        >
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#2FCC7C] mb-4">
            // 03 · Rules of the Game
          </div>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-tight">
            Submission <span className="text-gradient-neon">rules</span>.
          </h2>
          <p className="mt-6 text-lg text-zinc-400">
            Two constraints. Infinite room to be creative. Tap a card to expand.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="submission-rules-grid">
          {RULES.map((r, i) => {
            const Icon = r.icon;
            const isOpen = open === r.id;
            return (
              <motion.button
                key={r.id}
                type="button"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                onClick={() => setOpen(isOpen ? null : r.id)}
                data-testid={`rule-toggle-${r.id}`}
                className={`text-left glass rounded-2xl p-6 md:p-7 transition-all duration-400 hover:-translate-y-1 ${
                  isOpen
                    ? "border-[#2FCC7C]/60 shadow-[0_0_30px_rgba(47,204,124,0.2)]"
                    : "hover:border-white/20"
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div
                      className={`w-11 h-11 rounded-xl flex items-center justify-center border transition-colors ${
                        isOpen
                          ? "border-[#2FCC7C] bg-[#2FCC7C]/10"
                          : "border-white/10 bg-white/[0.02]"
                      }`}
                    >
                      <Icon
                        className={`w-5 h-5 transition-colors ${
                          isOpen ? "text-[#2FCC7C]" : "text-zinc-300"
                        }`}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
                        Rule 0{i + 1}
                      </div>
                      <h3 className="font-heading text-xl md:text-2xl font-bold tracking-tight">
                        {r.title}
                      </h3>
                      <p className="text-sm text-[#2FCC7C]/90 font-mono mt-1">
                        {r.short}
                      </p>
                    </div>
                  </div>
                  <div className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center flex-shrink-0">
                    {isOpen ? <Minus className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                  </div>
                </div>

                <div
                  className={`grid transition-all duration-500 ease-out ${
                    isOpen ? "grid-rows-[1fr] opacity-100 mt-5" : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="text-sm md:text-base text-zinc-400 leading-relaxed border-t border-white/5 pt-5">
                      {r.detail}
                    </p>
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>

        {/* Side note - duplicate / similar skills */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6 }}
          data-testid="rules-sidenote"
          className="mt-6 flex items-start gap-4 rounded-2xl border border-[#2FCC7C]/25 bg-[#2FCC7C]/[0.04] p-5 md:p-6"
        >
          <div className="w-9 h-9 rounded-lg border border-[#2FCC7C]/40 bg-[#2FCC7C]/10 flex items-center justify-center flex-shrink-0">
            <Clock className="w-4 h-4 text-[#2FCC7C]" strokeWidth={2} />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#2FCC7C] mb-1">
              Heads up · First come, first served
            </div>
            <p className="text-sm md:text-base text-zinc-300 leading-relaxed">
              If two or more submissions turn out to be{" "}
              <span className="text-white font-medium">similar skills</span>, only
              the one submitted <span className="text-white font-medium">earliest</span>{" "}
              will be considered. The rest will be ruled out. Ship early.
            </p>
          </div>
        </motion.div>

        {/* Repo link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between rounded-2xl glass p-5 md:p-6"
        >
          <div className="flex items-center gap-4">
            <div className="w-9 h-9 rounded-lg border border-white/10 bg-white/[0.02] flex items-center justify-center flex-shrink-0">
              <GitBranch className="w-4 h-4 text-zinc-300" strokeWidth={2} />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
                Submission repository
              </div>
              <p className="text-sm md:text-base text-zinc-300 font-mono">
                carestack/carestack.skills
              </p>
            </div>
          </div>
          <a
            href="https://gitlab.infra.aws.carestack.com/carestack/carestack.skills"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="rules-repo-link"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-[#2FCC7C]/40 text-[#2FCC7C] font-mono text-xs uppercase tracking-widest hover:bg-[#2FCC7C]/10 hover:border-[#2FCC7C] transition-colors whitespace-nowrap"
          >
            Open the repo
            <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2.5} />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
